import { invokeV4, tableRows } from "../../shared/api/v4Remote";
import type {
  Announcement,
  BusinessTarget,
  GovernanceAudit,
  GovernanceState,
  NotificationItem,
} from "./governance.types";
type Row = Record<string, unknown>;
const text = (value: unknown) => String(value ?? "");
export const remoteGovernanceRepository = {
  async getState(): Promise<GovernanceState> {
    const [targets, announcements, notifications, audit] = await Promise.all([
      tableRows<Row>("business_targets"),
      tableRows<Row>("announcements"),
      tableRows<Row>("notifications"),
      tableRows<Row>("audit_logs"),
    ]);
    return {
      targets: targets.map(
        (r) =>
          ({
            id: text(r.id),
            name: text(r.name),
            metric: r.metric,
            period: text(r.period),
            targetValue: Number(r.target_value),
            actualValue: Number(r.actual_value),
            scope: r.scope,
            storeId: r.store_id ?? null,
            createdBy: text(r.created_by),
            createdAt: text(r.created_at),
          }) as BusinessTarget,
      ),
      announcements: announcements.map(
        (r) =>
          ({
            id: text(r.id),
            title: text(r.title),
            content: text(r.content),
            audience: (r.audience ?? []) as Announcement["audience"],
            scope: r.scope,
            storeId: r.store_id ?? null,
            publishedBy: text(r.published_by),
            publishedAt: text(r.published_at),
            active: Boolean(r.active),
          }) as Announcement,
      ),
      notifications: notifications.map(
        (r) =>
          ({
            id: text(r.id),
            recipientId: text(r.recipient_id),
            storeId: r.store_id ? text(r.store_id) : null,
            type: r.type,
            title: text(r.title),
            content: text(r.content),
            entityType: text(r.entity_type),
            entityId: text(r.entity_id),
            createdAt: text(r.created_at),
            readAt: r.read_at ? text(r.read_at) : null,
          }) as NotificationItem,
      ),
      audit: audit.map(
        (r) =>
          ({
            id: text(r.id),
            actorId: text(r.actor_id),
            action: text(r.action),
            entityType: text(r.entity_type),
            entityId: text(r.entity_id),
            reason: text(r.reason),
            occurredAt: text(r.occurred_at),
          }) as GovernanceAudit,
      ),
    };
  },
  createTarget(_actor: unknown, input: Record<string, unknown>) {
    return invokeV4<{ id: string }>("v4-governance", "createTarget", input);
  },
  updateActual(_actor: unknown, targetId: string, actualValue: number) {
    return invokeV4<{ id: string }>("v4-governance", "updateTarget", {
      targetId,
      actualValue,
    });
  },
  publishAnnouncement(_actor: unknown, input: Record<string, unknown>) {
    return invokeV4<{ id: string }>(
      "v4-governance",
      "publishAnnouncement",
      input,
    );
  },
  recordExport(_actor: unknown, entity: string) {
    return invokeV4<{ id: string }>("v4-governance", "recordExport", {
      entity,
    });
  },
  markNotificationRead(_actor: unknown, notificationId: string) {
    return invokeV4<{ id: string }>("v4-governance", "markNotificationRead", {
      notificationId,
    });
  },
  safeDelete(
    _actor: unknown,
    input: { entityType: string; targetId: string; reason: string },
  ) {
    return invokeV4<{ id: string; mode: string; entityType: string }>(
      "v4-governance",
      "safeDelete",
      input,
    );
  },
};
